import { useEffect, useMemo, useState } from "react";
import { ThicknessBottle } from "../types";
import { toAppFileUrl } from "../utils/fileUrls";
import { useEscapeStack } from "../hooks/useEscapeStack";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useSwipeToDismiss } from "../hooks/useSwipeToDismiss";
import PlotBundlePanel from "./thickness/PlotBundlePanel";
import { buildShowAssetUrl, isShowMode } from "../showMode";
import ModalCloseButton from "./ModalCloseButton";
import {
  getAnalysisContractForViewport,
  hasPlotBundle,
  viewportClassFromWidth
} from "../utils/thicknessPlotBundle";

type Props = {
  open: boolean;
  bottle: ThicknessBottle | null;
  hydrating?: boolean;
  nasMountPath?: string;
  onClose: () => void;
};

function buildDetailPath(nasMountPath: string | undefined, bottle: ThicknessBottle | null) {
  if (!bottle) return "";
  const trialName = bottle.trial_name || "unknown";
  const relativePath = `thickness_plotter/${trialName}/output/bottle${bottle.relative_bottle_id}_thickness_plots.html`;
  if (isShowMode) {
    return buildShowAssetUrl(relativePath);
  }
  if (!nasMountPath) return "";
  const cleanedMount = nasMountPath.replace(/\\/g, "/").replace(/\/$/, "");
  return toAppFileUrl(`${cleanedMount}/${relativePath}`);
}

function buildSliceImagePaths(nasMountPath: string | undefined, bottle: ThicknessBottle | null) {
  if (!bottle) return { relative: "", absolute: "" };
  const trialName = bottle.trial_name || "unknown";
  const base = `thickness_plotter/${trialName}/output`;
  if (isShowMode) {
    return {
      relative: buildShowAssetUrl(
        `${base}/bottle${bottle.relative_bottle_id}_slices_profile_relative.png`
      ),
      absolute: buildShowAssetUrl(`${base}/bottle${bottle.relative_bottle_id}_slices_profile.png`)
    };
  }
  if (!nasMountPath) return { relative: "", absolute: "" };
  const cleanedMount = nasMountPath.replace(/\\/g, "/").replace(/\/$/, "");
  const mountedBase = `${cleanedMount}/${base}`;
  return {
    relative: toAppFileUrl(
      `${mountedBase}/bottle${bottle.relative_bottle_id}_slices_profile_relative.png`
    ),
    absolute: toAppFileUrl(`${mountedBase}/bottle${bottle.relative_bottle_id}_slices_profile.png`)
  };
}

export default function ThicknessDetailModal({
  open,
  bottle,
  hydrating = false,
  nasMountPath,
  onClose
}: Props) {
  const detailPath = useMemo(() => buildDetailPath(nasMountPath, bottle), [nasMountPath, bottle]);
  const sliceImages = useMemo(() => buildSliceImagePaths(nasMountPath, bottle), [nasMountPath, bottle]);
  const [viewportClass, setViewportClass] = useState(
    viewportClassFromWidth(typeof window === "undefined" ? 1400 : window.innerWidth)
  );
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  useEscapeStack(open && !!bottle, onClose);
  useEscapeStack(!!zoomSrc, () => setZoomSrc(null));
  useBodyScrollLock(open && !!bottle);
  const swipeToClose = useSwipeToDismiss<HTMLDivElement>({
    enabled: open && !!bottle,
    onDismiss: onClose
  });
  const swipeToCloseZoom = useSwipeToDismiss<HTMLDivElement>({
    enabled: !!zoomSrc,
    onDismiss: () => setZoomSrc(null)
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setViewportClass(viewportClassFromWidth(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!open || !bottle) return null;

  const analysisContract = getAnalysisContractForViewport(
    bottle.analysis_payload ? (bottle.analysis_payload as Record<string, unknown>) : null,
    viewportClass
  );
  const showBundle = hasPlotBundle(analysisContract);

  const imageFrameStyle = {
    display: "grid",
    placeItems: "center",
    marginTop: 10,
    padding: 12,
    background: "#0c1422",
    borderRadius: 12,
    border: "1px solid #1e2b3d",
    boxShadow: "0 12px 26px rgba(0,0,0,0.35)",
    minHeight: 260,
    height: "clamp(320px, 28vw, 420px)",
    overflow: "hidden"
  } as const;
  const imageStyle = {
    width: "100%",
    height: "100%",
    objectFit: "contain" as const,
    borderRadius: 10,
    border: "1px solid #243752",
    background: "#0a111c",
    cursor: "zoom-in"
  };

  const statRows = [
    { label: "Inner Volume", value: bottle.inner_volume, suffix: "cm³" },
    { label: "Outer Volume", value: bottle.outer_volume, suffix: "cm³" },
    { label: "Glass Volume", value: bottle.glass_volume, suffix: "cm³" },
    { label: "Glass Mass", value: bottle.glass_mass, suffix: "g" }
  ];

  const globalMin =
    bottle.global_min_z != null
      ? `(d=${fmtCoord(bottle.global_min_x)}°, h=${fmtCoord(bottle.global_min_y)} mm) @ t=${bottle.global_min_z.toFixed(
          2
        )} mm`
      : "—";
  const globalMax =
    bottle.global_max_z != null
      ? `(d=${fmtCoord(bottle.global_max_x)}°, h=${fmtCoord(bottle.global_max_y)} mm) @ t=${bottle.global_max_z.toFixed(
          2
        )} mm`
      : "—";

  return (
    <div className="modal-backdrop">
      <div ref={swipeToClose.modalRef} className="modal" style={{ width: "90vw", maxWidth: 1200, height: "90vh" }}>
        <div className="modal-swipe-handle" aria-hidden="true" {...swipeToClose.handleProps} />
        <div
          className="modal-drag-region"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          {...swipeToClose.handleProps}
        >
          <div>
            <h3 style={{ margin: 0 }}>
              {bottle.trial_name} — Bottle {bottle.relative_bottle_id}
            </h3>
            <p style={{ margin: 0, color: "var(--text-subtle)" }}>ID #{bottle.id}</p>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {showBundle && analysisContract ? (
          <div
            style={{
              marginTop: 12,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflowX: "clip"
            }}
          >
            <PlotBundlePanel analysis={analysisContract} />
          </div>
        ) : hydrating ? (
          <div
            style={{
              marginTop: 12,
              border: "1px dashed #2a3f5f",
              borderRadius: 14,
              padding: 18,
              color: "var(--text-muted)"
            }}
          >
            Loading modern plot bundle…
          </div>
        ) : (
          <>
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  border: "1px solid #1e2b3d",
                  borderRadius: 14,
                  background: "var(--surface-elevated)",
                  padding: 8,
                  width: "100%",
                  maxWidth: 1000,
                  margin: "0 auto",
                  aspectRatio: "2.1 / 1",
                  minHeight: 320,
                  maxHeight: 520,
                  overflow: "hidden",
                  display: "grid",
                  placeItems: "stretch"
                }}
              >
                {detailPath ? (
                  <iframe
                    src={detailPath}
                    title="Thickness detail"
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      borderRadius: 10,
                      background: "var(--surface-elevated)"
                    }}
                  />
                ) : (
                  <div
                    style={{
                      height: "100%",
                      display: "grid",
                      placeItems: "center",
                      color: "var(--text-faint)",
                      border: "1px dashed #2a3f5f",
                      borderRadius: 10
                    }}
                  >
                    {isShowMode
                      ? "Show asset not available for this bottle."
                      : "Mount the NAS to view thickness plots."}
                  </div>
                )}
              </div>
            </div>

            {detailPath && (
              <div className="responsive-grid" style={{ marginTop: 12 }}>
                <div
                  className="card"
                  style={{
                    background: "var(--surface-elevated)",
                    borderColor: "#1f334d"
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      color: "var(--text-primary)"
                    }}
                  >
                    <div
                      className="label"
                      style={{
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        display: "flex",
                        alignItems: "center",
                        gap: 8
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 999,
                          background: "var(--accent-one)",
                          boxShadow: "0 0 10px var(--accent-one)"
                        }}
                      />
                      Slices profile (relative)
                    </div>
                    <span style={{ fontSize: 11, color: "var(--text-faint)" }}>Click to zoom</span>
                  </div>
                  <div style={imageFrameStyle}>
                    <img
                      src={sliceImages.relative}
                      alt="Relative slices profile"
                      style={imageStyle}
                      onClick={() => setZoomSrc(sliceImages.relative)}
                    />
                  </div>
                </div>
                <div
                  className="card"
                  style={{
                    background: "var(--surface-elevated)",
                    borderColor: "#1f334d"
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      color: "var(--text-primary)"
                    }}
                  >
                    <div
                      className="label"
                      style={{
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        display: "flex",
                        alignItems: "center",
                        gap: 8
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 999,
                          background: "var(--accent-two)",
                          boxShadow: "0 0 10px var(--accent-two)"
                        }}
                      />
                      Slices profile
                    </div>
                    <span style={{ fontSize: 11, color: "var(--text-faint)" }}>Click to zoom</span>
                  </div>
                  <div style={imageFrameStyle}>
                    <img
                      src={sliceImages.absolute}
                      alt="Slices profile"
                      style={imageStyle}
                      onClick={() => setZoomSrc(sliceImages.absolute)}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <div className="card" style={{ marginTop: 12 }}>
          <div className="section-title" style={{ marginBottom: 8 }}>
            Statistics
          </div>
          <div className="grid two" style={{ gap: 10 }}>
            {statRows.map((row) => (
              <div
                key={row.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  border: "1px solid #1e2b3d",
                  borderRadius: 8,
                  background: "var(--surface-elevated)"
                }}
              >
                <span style={{ color: "var(--text-subtle)" }}>{row.label}</span>
                <span style={{ fontWeight: 600 }}>
                  {row.value != null ? `${row.value.toFixed(2)} ${row.suffix}` : "—"}
                </span>
              </div>
            ))}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 12px",
                border: "1px solid #1e2b3d",
                borderRadius: 8,
                background: "var(--surface-elevated)"
              }}
            >
              <span style={{ color: "var(--text-subtle)" }}>Global Min Thickness</span>
              <span style={{ fontWeight: 600 }}>{globalMin}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 12px",
                border: "1px solid #1e2b3d",
                borderRadius: 8,
                background: "var(--surface-elevated)"
              }}
            >
              <span style={{ color: "var(--text-subtle)" }}>Global Max Thickness</span>
              <span style={{ fontWeight: 600 }}>{globalMax}</span>
            </div>
          </div>
        </div>
      </div>

      {zoomSrc && (
        <div
          className="modal-backdrop"
          style={{ cursor: "zoom-out" }}
          onClick={() => setZoomSrc(null)}
        >
          <div
            ref={swipeToCloseZoom.modalRef}
            className="modal"
            style={{
              width: "90vw",
              maxWidth: 1400,
              height: "90vh",
              display: "grid",
              placeItems: "center",
              background: "var(--app-bg-base)",
              cursor: "zoom-out"
            }}
            onClick={() => setZoomSrc(null)}
          >
            <div
              className="modal-swipe-handle"
              aria-hidden="true"
              {...swipeToCloseZoom.handleProps}
            />
            <img
              src={zoomSrc}
              alt="Zoomed profile"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                borderRadius: 14,
                border: "1px solid #30486a",
                boxShadow: "0 20px 45px rgba(0,0,0,0.55)",
                cursor: "zoom-out"
              }}
            />
            <ModalCloseButton className="modal-close-button-floating" onClick={() => setZoomSrc(null)} />
          </div>
        </div>
      )}
    </div>
  );
}

function fmtCoord(v?: number) {
  return v != null ? v.toFixed(0) : "–";
}
